import React, { memo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface AdminTagChipPickerProps {
  tags: CorkBoardTag[];
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;
  maxTags?: number;
  label?: string;
  error?: string;
  lockedTag?: CorkBoardTag | null;
}

export const AdminTagChipPicker: React.FC<AdminTagChipPickerProps> = memo(
  ({
    tags,
    selectedTagIds,
    onToggleTag,
    maxTags = 1,
    label,
    error,
    lockedTag = null,
  }) => {
    const { redesign, semantic, admin } = useAdminColors();

    return (
      <View style={styles.container}>
        {label ? <Text style={[styles.label, { color: redesign.text.secondary }]}>{label}</Text> : null}

        <View style={styles.tagGrid}>
          {lockedTag ? (
            <View
              accessibilityLabel={`locked-tag-${lockedTag.id}`}
              style={[
                styles.tagChip,
                {
                  borderColor: lockedTag.color || redesign.accent.blueBg,
                  backgroundColor: admin.selector.itemSelectedBg,
                },
              ]}
            >
              {lockedTag.icon ? <Text style={styles.tagIcon}>{lockedTag.icon}</Text> : null}
              <Text style={[styles.tagName, { color: lockedTag.color || semantic.text.link }]}>
                {lockedTag.name}
              </Text>
              <Text style={styles.lockIcon}>🔒</Text>
            </View>
          ) : null}
          {tags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            return (
              <TouchableOpacity
                key={tag.id}
                accessibilityLabel={`tag-${tag.id}`}
                style={[
                  styles.tagChip,
                  {
                    borderColor: redesign.card.border,
                    backgroundColor: admin.formInput.bg,
                  },
                  isSelected && {
                    borderColor: tag.color || redesign.accent.blueBg,
                    backgroundColor: admin.selector.itemSelectedBg,
                  },
                ]}
                onPress={() => onToggleTag(tag.id)}
              >
                {tag.icon ? <Text style={styles.tagIcon}>{tag.icon}</Text> : null}
                <Text
                  style={[
                    styles.tagName,
                    { color: redesign.text.primary },
                    isSelected && { color: tag.color || semantic.text.link },
                  ]}
                >
                  {tag.name}
                </Text>
                {/* The slot is always rendered so a selected chip is exactly as wide as its
                    unselected self. Mounting the check only on selection grew the chip by
                    18px (icon + gap), which reflowed the wrap grid and bumped chips onto
                    new rows every time the selection changed. */}
                <View testID={`tag-${tag.id}-indicator`} style={styles.indicatorSlot}>
                  {isSelected && (
                    <SVG_COMPONENTS.CHECK_CIRCLE width={12} height={12} color={redesign.accent.blueBg} />
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {error ? <Text style={[styles.errorText, { color: semantic.status.error }]}>{error}</Text> : null}
        <Text style={[styles.hint, { color: redesign.text.subtle }]}>
          {selectedTagIds.length} of {maxTags} tags selected
        </Text>
      </View>
    );
  },
);

AdminTagChipPicker.displayName = 'AdminTagChipPicker';

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: '500',
  },
  tagGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  tagIcon: {
    fontSize: 14,
  },
  tagName: {
    fontSize: 13,
    fontWeight: '500',
  },
  indicatorSlot: {
    width: 12,
    height: 12,
  },
  errorText: {
    fontSize: 12,
  },
  hint: {
    fontSize: 12,
  },
  lockIcon: {
    fontSize: 11,
  },
});
